/**
 * The 40 languages from the Obsidian language list.
 * A language is available when its course can actually be opened.
 */
import { courseHasContent } from "../features/course/content.js";
import { visibleCourseTargetLangs } from "./course-targets.js";
import { getTargetLandingSegment, pathPrefixForUiLang } from "./url-i18n.js";
import { courseAudioIndicator } from "./course-audio-status.js";

export const LANGUAGE_CATALOGUE = Object.freeze([
  { code: "bg", blurb: "Bulgarian is a South Slavic language spoken in Bulgaria. It is written in Cyrillic and is one of the official languages of the European Union." },
  { code: "hr", blurb: "Croatian is a South Slavic language spoken in Croatia and neighbouring countries, and written in the Latin alphabet." },
  { code: "cs", blurb: "Czech is a West Slavic language spoken in Czechia, closely related to Slovak." },
  { code: "da", blurb: "Danish is a North Germanic language spoken in Denmark and in Danish communities abroad." },
  { code: "nl", blurb: "Dutch is a West Germanic language spoken in the Netherlands and in Flanders." },
  { code: "en", blurb: "English is a West Germanic language used as a first language in the United Kingdom, Ireland, the United States and many other countries." },
  { code: "et", blurb: "Estonian is a Finnic language spoken in Estonia and related to Finnish." },
  { code: "fi", blurb: "Finnish is a Finnic language spoken mainly in Finland." },
  { code: "fr", blurb: "French is a Romance language spoken in France, Belgium, Switzerland, Canada and across many other countries." },
  { code: "de", blurb: "German is a West Germanic language spoken by more than 100 million people in Germany, Austria and Switzerland." },
  { code: "el", blurb: "Greek is the language of Greece and Cyprus, written in the Greek alphabet and the only living member of the Hellenic branch." },
  { code: "hu", blurb: "Hungarian is a Uralic language spoken in Hungary and in neighbouring regions of Central Europe." },
  { code: "ga", blurb: "Irish is a Celtic language of Ireland, still a community language along the west coast and taught throughout the country." },
  { code: "it", blurb: "Italian is a Romance language spoken in Italy, parts of Switzerland and communities around the Mediterranean." },
  { code: "lv", blurb: "Latvian is a Baltic language spoken in Latvia." },
  { code: "lt", blurb: "Lithuanian is a Baltic language spoken in Lithuania and one of the older living branches of Indo-European." },
  { code: "mt", blurb: "Maltese is a Semitic language of Malta, written in the Latin alphabet and shaped by long contact with Italian and English." },
  { code: "pl", blurb: "Polish is a West Slavic language spoken in Poland and in large communities abroad." },
  { code: "pt", blurb: "Portuguese is a Romance language spoken in Portugal, Brazil and several countries in Africa and Asia." },
  { code: "ro", blurb: "Romanian is a Romance language spoken in Romania and Moldova." },
  { code: "sk", blurb: "Slovak is a West Slavic language spoken in Slovakia and closely related to Czech." },
  { code: "sl", blurb: "Slovenian is a South Slavic language spoken in Slovenia." },
  { code: "es", blurb: "Spanish is a Romance language spoken in Spain and across most of Latin America." },
  { code: "sv", blurb: "Swedish is a North Germanic language spoken in Sweden and in parts of Finland." },
  { code: "sq", blurb: "Albanian forms its own branch of Indo-European and is spoken in Albania, Kosovo and neighbouring communities." },
  { code: "ar", blurb: "Arabic is a Semitic language spoken across North Africa and the Middle East, with a shared literary language used from Morocco to the Gulf." },
  { code: "hy", blurb: "Armenian forms its own branch of Indo-European. It is spoken in Armenia and in diaspora communities around the world." },
  { code: "zh", blurb: "Chinese, in the Mandarin standard, is a Sinitic language spoken across China, Taiwan and many communities abroad." },
  { code: "ka", blurb: "Georgian is a Kartvelian language spoken in Georgia and written in its own alphabet." },
  { code: "hi", blurb: "Hindi is an Indo-Aryan language used mainly in northern and central India and in communities abroad." },
  { code: "id", blurb: "Indonesian is the national language of Indonesia, an Austronesian language used from Sumatra to Papua." },
  { code: "ja", blurb: "Japanese is the language of Japan, written with hiragana, katakana and kanji." },
  { code: "ko", blurb: "Korean is the language of the Korean peninsula, written in Hangul." },
  { code: "ne", blurb: "Nepali is an Indo-Aryan language spoken in Nepal and in communities of northern India." },
  { code: "fa", blurb: "Persian is an Iranian language spoken in Iran and, in closely related forms, in Afghanistan and Tajikistan." },
  { code: "ru", blurb: "Russian is an East Slavic language spoken in Russia and across a wide area of northern Eurasia." },
  { code: "sw", blurb: "Swahili is a Bantu language of the East African coast and a shared language of Tanzania, Kenya and neighbouring countries." },
  { code: "th", blurb: "Thai is a Tai language spoken in Thailand and written in its own alphabet." },
  { code: "tr", blurb: "Turkish is a Turkic language spoken in Turkey and Cyprus and written in the Latin alphabet." },
  { code: "vi", blurb: "Vietnamese is a Vietic language spoken in Vietnam and written in a Latin-based alphabet." },
]);

const CATALOGUE_CODES = new Set(LANGUAGE_CATALOGUE.map((item) => item.code));

export function isCatalogueLanguage(code) {
  return CATALOGUE_CODES.has(String(code || "").trim().toLowerCase());
}

export function languageBlurb(code) {
  const c = String(code || "").trim().toLowerCase();
  return LANGUAGE_CATALOGUE.find((item) => item.code === c)?.blurb || "";
}

/** A homepage language is listed when its course is public and can be opened. */
export function languageCourseAvailable(code) {
  const c = String(code || "").trim().toLowerCase();
  return visibleCourseTargetLangs().includes(c) && courseHasContent(c);
}

export function languageStartHref(uiLang, code) {
  const segment = getTargetLandingSegment(uiLang, code);
  if (!segment) return "";
  const prefix = pathPrefixForUiLang(uiLang);
  return `${prefix}/${segment}`.replace(/\/+/g, "/");
}

export function homeLanguageButtons(uiLang, nameFor) {
  const buttons = LANGUAGE_CATALOGUE.map((item) => {
    const available = languageCourseAvailable(item.code);
    return {
      targetCode: item.code,
      label: nameFor(item.code) || item.code.toUpperCase(),
      href: available ? languageStartHref(uiLang, item.code) : "",
      available,
      audioStatus: courseAudioIndicator(item.code, uiLang),
    };
  });
  const order = visibleCourseTargetLangs();
  const rank = (code) => {
    const index = order.indexOf(code);
    return index === -1 ? order.length : index;
  };
  const listed = buttons.filter((button) => button.available).sort((a, b) => rank(a.targetCode) - rank(b.targetCode));
  // Swiss German stays on the homepage beside German. It is not one of the new course targets.
  if (!listed.some((button) => button.targetCode === "ch")) {
    const href = languageStartHref(uiLang, "ch");
    if (href) {
      const swiss = {
        targetCode: "ch",
        label: nameFor("ch") || "ch",
        href,
        available: true,
        audioStatus: courseAudioIndicator("ch", uiLang),
      };
      const german = listed.findIndex((button) => button.targetCode === "de");
      listed.splice(german === -1 ? 0 : german + 1, 0, swiss);
    }
  }
  return listed;
}

const capitalize = (text, lang) => text.charAt(0).toLocaleUpperCase(lang) + text.slice(1);

/** Homepage: German stands alone; every other course follows, sorted for the interface language. */
export function homeLanguageGroups(buttons, uiLang) {
  const german = buttons.find((button) => button.targetCode === "de") || null;
  const swiss = buttons.find((button) => button.targetCode === "ch") || null;
  const collator = new Intl.Collator(uiLang);
  const others = buttons
    .filter((button) => button !== german && button !== swiss)
    .map((button) => ({ ...button, label: capitalize(String(button.label), uiLang) }))
    .sort((a, b) => collator.compare(a.label, b.label));
  return { german, swiss, others };
}
